import { hasRole, models, pagePermissions } from 'cesg-shared';
import { UserRole } from 'cesg-shared/dist/models/index.js';
import { computed, type Ref } from 'vue';
import type {
  AtaDetailsPermissions,
  AtaFieldKey,
  InputState,
} from '../../types';

interface UseAtaDetailsPermissionsProps {
  status: Ref<models.domain.AtaStatusType | null | undefined>;
  user: models.GetMeResponse | null;
  projectId: string;
}

// Statuses that lock the form down to read-only.
const READ_ONLY_STATUSES: models.domain.AtaStatusType[] = [
  models.domain.AtaStatusType.Done,
  models.domain.AtaStatusType.InvoicingOk,
  models.domain.AtaStatusType.InProgress,
  models.domain.AtaStatusType.Approved,
  models.domain.AtaStatusType.Rejected,
];

const CESG_AVAILABLE_STATUSES: models.domain.AtaStatusType[] = [
  models.domain.AtaStatusType.UnderDevelopment,
  models.domain.AtaStatusType.OnReferral,
  models.domain.AtaStatusType.Approved,
  models.domain.AtaStatusType.Rejected,
  models.domain.AtaStatusType.InProgress,
  models.domain.AtaStatusType.Done,
  models.domain.AtaStatusType.InvoicingOk,
];

export const useAtaDetailsPermissions = ({
  status,
  user,
  projectId,
}: UseAtaDetailsPermissionsProps) => {
  const isGuestUser = user ? !hasRole(UserRole.CESG, user) : true;

  return computed<AtaDetailsPermissions>(() => {
    const permissionTo = pagePermissions(projectId, user)?.ata;

    const readOnly = READ_ONLY_STATUSES.includes(
      status.value as models.domain.AtaStatusType,
    );

    const availableStatuses = isGuestUser
      ? models.domain.GUEST_EDITABLE_ATA_STATUSES
      : CESG_AVAILABLE_STATUSES;

    const statusNoGoingBack =
      isGuestUser &&
      !models.domain.GUEST_EDITABLE_ATA_STATUSES.includes(
        status.value as models.domain.AtaStatusType,
      );

    // Every field but `answerReceivedDate` is locked once the ATA is read-only.
    const gated = (canEdit: boolean | undefined): InputState =>
      readOnly || !canEdit ? 'read-only' : 'edit';

    const ataField = gated(permissionTo?.edit.ata);

    const fields: Record<AtaFieldKey, InputState> = {
      title: ataField,
      description: gated(permissionTo?.edit.description),
      answer: gated(permissionTo?.edit.answer),
      shouldBeRegulated: ataField,
      leadsToTimeExtension: ataField,
      pricingModel: ataField,
      shouldBeForecasted: ataField,
      type: ataField,
      priority: ataField,
      sentDate: ataField,
      deadlineDate: ataField,
      // Editable for EDIT users even when the rest of the form is read-only, so
      // admins can still correct the answer-received date on a decided ÄTA.
      answerReceivedDate: permissionTo?.edit.ata ? 'edit' : 'read-only',
      revisionDate: ataField,
      costs: ataField,
      documents:
        !isGuestUser &&
        permissionTo?.edit.ata &&
        status.value === models.domain.AtaStatusType.UnderDevelopment
          ? 'edit'
          : 'read-only',
    };

    return {
      readOnly,
      isGuestUser,
      availableStatuses,
      fields,
      status:
        permissionTo?.edit.status && !statusNoGoingBack ? 'edit' : 'read-only',
      save: permissionTo?.save ? 'visible' : 'hidden',
    };
  });
};
